"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = IdleLockScreen;
var _react = _interopRequireWildcard(require("react"));
var _idleClock = require("./idleClock");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
/**
 * IdleLockScreen — what a tab shows once the session has ended.
 *
 * ── Why this replaces the app instead of blurring it ──
 *
 * Fading and blurring remove nothing. The text is still in the page and still
 * in memory, and anybody who opens the developer tools reads it in full. A blur
 * stops confusion, not an attacker.
 *
 * So the app unmounts and this renders in its place. The person sees a dark,
 * quiet, plainly-not-working screen — the same look a fade would give — but
 * there is nothing behind it to read.
 *
 * The app is responsible for not rendering its own tree when `isLocked` is
 * true. This component only draws the screen.
 *
 * ── Why it does not offer to sign in here ──
 *
 * By decision: a locked tab sends the person back to the Console. The other
 * tabs stay locked until they are reloaded. That is why the draft save at 28
 * minutes matters so much — it is the only thing standing between an author and
 * a lost afternoon.
 *
 * ── Why it names what was open ──
 *
 * Console is the only app with a sign-in form, so a link back to it from
 * Channels, Designer, or Explorer only ever lands on Console — never back on
 * the work this tab had open, which the sign-out already unmounted. That link
 * told the person where they could go, not what they'd be going back to fix.
 *
 * `contextLabel` is the thing to decide with: which survey, which channel,
 * whichever the app can still name from before it unmounted. Seeing it is
 * what lets a person choose between signing in again and just closing the
 * tab — a choice they cannot make from a blank "you were signed out."
 */function IdleLockScreen({
  logo,
  appName,
  reason,
  signInUrl,
  saving = false,
  saveResults = [],
  contextLabel = null,
  idleMinutes = 30
}) {
  const headingRef = (0, _react.useRef)(null);

  // Take focus, so the keyboard cannot still be somewhere in the old screen.
  (0, _react.useEffect)(() => {
    if (headingRef.current) {
      try {
        headingRef.current.focus();
      } catch (err) {/* focus is a nicety */}
    }
  }, []);
  const signedOutOnPurpose = reason === _idleClock.END_REASON_SIGNOUT;
  const saved = saveResults.filter(r => r && r.saved);
  const failed = saveResults.filter(r => r && r.saved === false);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    className: "idle-lock",
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": "idle-lock-title",
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: "idle-lock__card",
      children: [logo && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        className: "idle-lock__logo",
        children: logo
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("h1", {
        className: "idle-lock__title",
        id: "idle-lock-title",
        tabIndex: -1,
        ref: headingRef,
        children: signedOutOnPurpose ? 'You have signed out.' : 'You have been signed out.'
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "idle-lock__body",
        children: signedOutOnPurpose ? `Signing out of ${appName || 'Sam Surveyor'} in one tab signs you out of all of them.` : `Sam Surveyor signs you out after ${idleMinutes} minutes without activity.`
      }), saving && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "idle-lock__body idle-lock__saving",
        children: "Saving your work\u2026"
      }), !saving && saved.length > 0 && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "idle-lock__saved",
        children: savedSentence(saved)
      }), !saving && failed.length > 0 && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "idle-lock__failed",
        role: "alert",
        children: failedSentence(failed)
      }), !saving && saved.length === 0 && failed.length === 0 && contextLabel && /*#__PURE__*/(0, _jsxRuntime.jsxs)("p", {
        className: "idle-lock__context",
        children: ["You were working on ", /*#__PURE__*/(0, _jsxRuntime.jsx)("strong", {
          children: contextLabel
        }), "."]
      }), signInUrl && /*#__PURE__*/(0, _jsxRuntime.jsx)("a", {
        className: "idle-lock__action",
        href: signInUrl,
        children: "Go to sign in"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "idle-lock__note",
        children: "You can close this tab."
      })]
    })
  });
}

/** Say what was saved and where, without inventing anything. */
function savedSentence(saved) {
  const local = saved.filter(r => r.where !== 'cloud');
  const cloud = saved.filter(r => r.where === 'cloud');
  const named = saved.map(r => r.label).filter(Boolean);
  const what = named.length === 1 ? `“${named[0]}”` : 'Your unsaved work';
  if (cloud.length > 0 && local.length > 0) {
    return `${what} was saved as a draft, and a copy is kept in this browser.`;
  }
  if (cloud.length > 0) {
    return `${what} was saved as a draft version.`;
  }
  return `${what} is kept in this browser. Sign in again here to pick it up.`;
}

/** Say plainly that a save failed, and what is still there. */
function failedSentence(failed) {
  const anyLocal = failed.some(r => r.where !== 'cloud');
  if (anyLocal) {
    return 'Some unsaved work could not be kept. This browser refused to store it.';
  }
  return 'Your work could not be saved to the cloud. A copy is kept in this browser.';
}